import { useState, useI18n } from '#imports';
import type { CustomText } from '@/api/sensyn-id/generated/model';
import { useSensynIdApi } from '@/composables/use-sensyn-id-api';
import { useLogger } from '@/composables/use-logger';

const DEFAULT_APPLICATION = 'datastore-v3';
const VALID_LOGO_PREFIXES = [
  'data:image/png',
  'data:image/jpeg',
  'data:image/gif',
  'data:image/webp',
  'data:image/x-icon',
  'data:image/svg+xml',
  'https://',
];
const CACHE_KEY_PREFIX = 'customText:';

function getCacheKey(): string | null {
  try {
    const orgId = localStorage.getItem('lastAccessOrganizationId');
    return orgId ? `${CACHE_KEY_PREFIX}${orgId}` : null;
  } catch {
    return null;
  }
}

export function clearCustomTextCache(): void {
  try {
    const key = getCacheKey();
    if (key) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable
  }
}

export function useCustomText() {
  const customText = useState<CustomText | null>('customText', () => null);
  const loaded = useState<boolean>('customTextLoaded', () => false);
  const cacheRestored = useState<boolean>('customTextCacheRestored', () => false);
  const { locale } = useI18n();

  // キャッシュからの復元（同期、初回のみ）
  if (!cacheRestored.value) {
    cacheRestored.value = true;
    try {
      const key = getCacheKey();
      if (key) {
        const cached = localStorage.getItem(key);
        if (cached) {
          customText.value = JSON.parse(cached) as CustomText;
        }
      }
    } catch {
      // cache parse error
    }
  }

  function getLang(): string {
    return locale.value || 'ja';
  }

  async function fetchCustomText() {
    if (loaded.value) return;
    try {
      const { useOrganizationApi } = useSensynIdApi();
      const response = await useOrganizationApi().getCustomTexts({
        application: DEFAULT_APPLICATION,
        lang: getLang(),
      });
      customText.value = response.data;
      // キャッシュ更新
      try {
        const key = getCacheKey();
        if (key) {
          localStorage.setItem(key, JSON.stringify(response.data));
        }
      } catch {
        // localStorage full or unavailable
      }
    } catch (error) {
      customText.value = null;
      const logger = useLogger();
      logger.error({ error }, '[use-custom-text] Failed to fetch custom text');
    } finally {
      loaded.value = true;
    }
  }

  function getCustomAppTitle(): string | null {
    const title = customText.value?.custom_app_title?.trim();
    return title || null;
  }

  function getCustomAppLogo(): string | null {
    const logo = customText.value?.custom_app_logo;
    if (!logo) return null;
    if (!VALID_LOGO_PREFIXES.some((prefix) => logo.startsWith(prefix))) return null;
    return logo;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  function getOtherTexts(): Record<string, any> {
    return (customText.value?.other_texts ?? {}) as Record<string, any>;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return {
    customText,
    loaded,
    fetchCustomText,
    getCustomAppTitle,
    getCustomAppLogo,
    getOtherTexts,
    getLang,
  };
}
